import {
    Component,
    DestroyRef,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule, type ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import type { TablePageEvent } from 'primeng/table';
import type { MenuItem, SortEvent } from 'primeng/api';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import type { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import { UIRouterModule } from '@uirouter/angular';
import { StateService } from '@uirouter/core';
import { ParamService } from '@monsido/core/param/param.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { STATISTICS } from '@client/app/shared-modules/statistics/statistics.constant';
import {
    StatisticsDateRangeService,
} from '@monsido/modules/statistics/services/date-range/statistics-date-range.service';
import { AVAILABLE_PAGE_SIZE, DEFAULT_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { StatisticsExportBuilderService } from '@monsido/modules/statistics/services/export/statistics-export-builder.service';
import type { CreateExport } from '@client/app/shared-modules/create-export/types/create-export.type';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { BreadcrumbComponent } from '@client/app/feature-modules/shared/components/breadcrumb/breadcrumb.component';
import {
    TableWrapperComponent,
    TableWrapperHeader,
} from '@client/app/shared-modules/table/components/table-wrapper.component';
import { SvgIconComponent } from '@client/app/shared-modules/svg-icons/components/svg-icon/svg-icon.component';
import {
    SiteAnalyticsCellValue,
    SiteAnalyticsColumnConfig,
    SiteAnalyticsDataResult,
    SiteAnalyticsDateRange,
    SiteAnalyticsLinkCell,
    SiteAnalyticsExportVariantConfig,
    SiteAnalyticsHierarchicalRowsConfig,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsSortDirection,
    SiteAnalyticsStateLinkCell,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import { MOST_POPULAR_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/most-popular-page.config';
import { SiteAnalyticsPageDataService } from '@client/app/feature-modules/site-analytics/services/site-analytics-page-data.service';
import {
    SiteAnalyticsDateRangePickerComponent,
} from '@client/app/feature-modules/site-analytics/components/date-range-picker/date-range-picker.component';

@Component({
    selector: 'mon-statistics-page-scaffold',
    standalone: true,
    templateUrl: './statistics-page-scaffold.component.html',
    styleUrls: ['./statistics-page-scaffold.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        TranslocoModule,
        UIRouterModule,
        PageLayoutComponent,
        BreadcrumbComponent,
        CardModule,
        ChartModule,
        SiteAnalyticsDateRangePickerComponent,
        SelectModule,
        ToggleSwitchModule,
        ButtonModule,
        TableWrapperComponent,
        SvgIconComponent,
    ],
})
export class StatisticsPageScaffoldComponent implements OnInit {
    @Input() config: SiteAnalyticsScaffoldConfig = MOST_POPULAR_PAGE_CONFIG;

    private readonly translocoService = inject(TranslocoService);
    private readonly stateService = inject(StateService);
    private readonly paramService = inject(ParamService);
    private readonly dateRangeService = inject(DateRangeService);
    private readonly statisticsDateRangeService = inject(StatisticsDateRangeService);
    private readonly statisticsExportBuilderService = inject(StatisticsExportBuilderService);
    private readonly siteAnalyticsPageDataService = inject(SiteAnalyticsPageDataService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);

    rows: SiteAnalyticsTableRow[] = [];
    total = 0;
    page = 1;
    perPage = DEFAULT_PAGE_SIZE;
    loading = false;
    searchTerm = '';
    sortBy = this.config.defaultSortBy;
    sortDir: SiteAnalyticsSortDirection = this.config.defaultSortDir;
    columnAggregates: Record<string, string | null> = {};

    domainId = 0;
    chartData: ChartData<'bar'> = {
        labels: [],
        datasets: [],
    };

    chartOptions: ChartOptions<'bar'> = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
    };

    primaryDateRange: [Date | null, Date | null] | null = null;
    comparisonDateRange: [Date | null, Date | null] | null = null;
    comparisonEnabled = false;

    selectedExportVariantId: string | null = null;
    breadcrumbItems: MenuItem[] = [];

    private routeParams: Record<string, unknown> = {};
    private nonComparisonPerPage = DEFAULT_PAGE_SIZE;

    private readonly destroy$ = new Subject<void>();
    private readonly searchSubject = new Subject<string>();
    private readonly expandedHierarchyParentIds = new Set<string>();

    get visibleColumns (): SiteAnalyticsColumnConfig[] {
        return this.config.columns.filter((column) => {
            const visibility = column.visibility || 'always';
            if (visibility === 'comparison') {
                return this.isComparisonModeActive;
            }
            return true;
        });
    }

    get tableRows (): SiteAnalyticsTableRow[] {
        if (!this.hierarchicalRowsConfig) {
            return this.rows;
        }

        const {
            rowTypeField,
            rowTypeChildValue,
            parentRowIdField,
        } = this.hierarchicalRowsConfig;

        return this.rows.filter((row) => {
            const rowType = this.getRowStringValue(row, rowTypeField);
            if (rowType !== rowTypeChildValue) {
                return true;
            }

            const parentId = this.getRowStringValue(row, parentRowIdField);
            return Boolean(parentId && this.expandedHierarchyParentIds.has(parentId));
        });
    }

    get tableHeaders (): TableWrapperHeader<SiteAnalyticsTableRow>[] {
        const columns = this.visibleColumns;

        return columns.map((column) => {
            return {
                id: column.id,
                label: this.translocoService.translate(column.headerKey),
                secondaryLabel: this.columnAggregates[column.id] ?? null,
                type: 'custom',
                sortable: column.sortable,
                sortField: column.sortField,
                width: column.width,
                cellClass: column.visibility === 'comparison' ? 'comparison-column' : undefined,
            } as TableWrapperHeader<SiteAnalyticsTableRow>;
        });
    }

    get totalRecords (): number {
        // The stats API returns a per-page count, not a grand total. Derive the
        // paginator count from the current page and returned rows instead.
        const base = (this.page - 1) * this.perPage + this.rows.length;
        return this.rows.length >= this.perPage ? base + 1 : base;
    }

    get searchResultsAnnouncement (): string | null {
        if (!this.searchTerm) {
            return null;
        }

        return this.translocoService.translate('siteAnalytics.scaffold.table.results{{count}}', {
            count: this.total,
        });
    }

    get isSearchSupported (): boolean {
        return this.config.searchSupported !== false;
    }

    get hasChartData (): boolean {
        return Boolean(this.chartData.labels?.length && this.chartData.datasets?.length);
    }

    get chartEmptyMessageKey (): string {
        return this.isSearchSupported && this.searchTerm
            ? 'siteAnalytics.summary.shared.emptySearchState'
            : 'siteAnalytics.summary.shared.emptyState';
    }

    get shouldShowChart (): boolean {
        return this.config.showChart !== false;
    }

    get showExportVariantSelector (): boolean {
        return this.isExportEnabled && (this.config.exportVariants?.length || 0) > 1;
    }

    get exportVariantOptions (): Array<{ label: string; id: string }> {
        return (this.config.exportVariants || []).map((variant) => ({
            id: variant.id,
            label: this.translocoService.translate(variant.labelKey),
        }));
    }

    get selectedExportVariant (): SiteAnalyticsExportVariantConfig | null {
        if (!this.selectedExportVariantId) {
            return this.config.exportVariants?.[0] || null;
        }

        return this.config.exportVariants?.find((variant) => variant.id === this.selectedExportVariantId) || null;
    }

    get hierarchicalRowsConfig (): SiteAnalyticsHierarchicalRowsConfig | null {
        return this.config.hierarchicalRows || null;
    }

    get exportConfig (): CreateExport | null {
        if (!this.isExportEnabled || !this.hasCompleteDateRange(this.primaryDateRange) || !this.domainId) {
            return null;
        }

        const [fromDate, toDate] = this.primaryDateRange;
        const categoryRefSegment = this.selectedExportVariant?.categoryRefSegment ?? this.config.exportCategoryRefMaxItems;
        const categoryRef = categoryRefSegment === undefined
            ? this.statisticsExportBuilderService.buildCategoryRef(
                moment(fromDate),
                moment(toDate),
                STATISTICS.DATE_FORMAT,
            )
            : this.statisticsExportBuilderService.buildCategoryRef(
                moment(fromDate),
                moment(toDate),
                STATISTICS.DATE_FORMAT,
                categoryRefSegment,
            );

        const selectedExportCategory = this.selectedExportVariant?.category || this.config.exportCategory;

        return {
            buttonLabel: this.translocoService.translate('siteAnalytics.scaffold.export.createDialogButtonLabel'),
            payload: {
                category: selectedExportCategory,
                category_ref: categoryRef,
                source_id: this.domainId,
                isGlobalExport: true,
                params: {},
            },
        };
    }

    get isComparisonSupported (): boolean {
        return this.config.comparisonEnabled !== false;
    }

    get isExportEnabled (): boolean {
        return Boolean(this.config.exportCategory);
    }

    get isComparisonModeActive (): boolean {
        return this.isComparisonSupported && this.comparisonEnabled && this.hasCompleteDateRange(this.comparisonDateRange);
    }

    ngOnInit (): void {
        this.initializeExportVariants();

        const params = this.paramService.getParams() as Record<string, unknown>;
        this.applyParams(params);
        this.initializeChartOptions();
        this.loadData();

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((updated) => {
            this.applyParams(updated as Record<string, unknown>);
            this.loadData();
        }) as (() => void);

        this.searchSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((searchText) => {
                this.paramService.updateParams({
                    search: searchText,
                    page: 1,
                });
            });

        this.destroyRef.onDestroy(() => {
            this.destroy$.next();
            this.destroy$.complete();
            deregisterHook();
        });
    }

    onSearchInput (event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.searchSubject.next(value);
    }

    onExportVariantChange (variantId: string): void {
        this.selectedExportVariantId = variantId;
    }

    isHierarchyToggleColumn (field: string): boolean {
        return Boolean(
            this.hierarchicalRowsConfig &&
            field === this.hierarchicalRowsConfig.toggleColumnId,
        );
    }

    isHierarchyParentRow (row: SiteAnalyticsTableRow): boolean {
        if (!this.hierarchicalRowsConfig) {
            return false;
        }

        const rowType = this.getRowStringValue(row, this.hierarchicalRowsConfig.rowTypeField);
        return rowType === this.hierarchicalRowsConfig.rowTypeParentValue;
    }

    isHierarchyExpanded (row: SiteAnalyticsTableRow): boolean {
        return this.expandedHierarchyParentIds.has(row.id);
    }

    getHierarchyToggleAriaLabel (row: SiteAnalyticsTableRow): string {
        const hierarchyConfig = this.hierarchicalRowsConfig;
        const firstDataColumn = this.visibleColumns.find((column) => column.id !== hierarchyConfig?.toggleColumnId);
        const label = firstDataColumn
            ? this.getTextCellValue(row, firstDataColumn.id)
            : row.id;

        return this.translocoService.translate('siteAnalytics.scaffold.table.toggleChildrenAriaLabel{{label}}', {
            label,
        });
    }

    toggleHierarchyRow (row: SiteAnalyticsTableRow): void {
        if (!this.isHierarchyParentRow(row)) {
            return;
        }

        if (this.expandedHierarchyParentIds.has(row.id)) {
            this.expandedHierarchyParentIds.delete(row.id);
            return;
        }

        this.expandedHierarchyParentIds.add(row.id);
    }

    onHierarchyToggleKeyDown (event: Event, row: SiteAnalyticsTableRow): void {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleHierarchyRow(row);
        }
    }

    onSort (event: SortEvent): void {
        const { field, order } = event;
        if (!field) {
            return;
        }

        const newDirection: SiteAnalyticsSortDirection = order === 1 ? 'asc' : 'desc';

        if (field === this.sortBy && newDirection === this.sortDir) {
            return;
        }

        this.paramService.updateParams({
            sort_by: field,
            sort_dir: newDirection,
            page: 1,
        });
    }

    onPageChange (event: TablePageEvent): void {
        const rows = this.isComparisonModeActive ? this.config.comparisonMaxPageSize : event.rows;
        const pageSizeChanged = rows !== this.perPage;
        const newPage = pageSizeChanged ? 1 : Math.floor((event.first || 0) / (rows || 1)) + 1;

        if (newPage === this.page && rows === this.perPage) {
            return;
        }

        if (!this.isComparisonModeActive) {
            this.nonComparisonPerPage = rows;
        }

        this.paramService.updateParams({
            page: newPage,
            page_size: rows,
        });
    }

    onPrimaryDateRangeChange (value: [Date | null, Date | null] | null): void {
        this.primaryDateRange = value;

        if (!this.hasCompleteDateRange(value)) {
            return;
        }

        this.rememberPrimaryDateRange();
        this.paramService.updateParams({
            from: this.toDateParam(value[0]),
            to: this.toDateParam(value[1]),
            page: 1,
        });
    }

    onComparisonToggleChange (event: ToggleSwitchChangeEvent): void {
        if (!this.isComparisonSupported) {
            return;
        }

        this.comparisonEnabled = Boolean(event.checked);

        if (!this.comparisonEnabled) {
            this.comparisonDateRange = null;
            this.paramService.updateParams({
                comparison: 'false',
                compare_from: '',
                compare_to: '',
                page: 1,
                page_size: this.nonComparisonPerPage,
            });
            return;
        }

        if (this.hasCompleteDateRange(this.comparisonDateRange)) {
            this.paramService.updateParams({
                comparison: 'true',
                compare_from: this.toDateParam(this.comparisonDateRange[0]),
                compare_to: this.toDateParam(this.comparisonDateRange[1]),
                page: 1,
                page_size: this.config.comparisonMaxPageSize,
            });
            return;
        }

        this.paramService.updateParams({
            comparison: 'true',
            compare_from: '',
            compare_to: '',
            page: 1,
            page_size: this.nonComparisonPerPage,
        });
    }

    onComparisonDateRangeChange (value: [Date | null, Date | null] | null): void {
        if (!this.isComparisonSupported) {
            return;
        }

        this.comparisonDateRange = value;

        if (!this.comparisonEnabled) {
            return;
        }

        if (!this.hasCompleteDateRange(value)) {
            this.paramService.updateParams({
                compare_from: '',
                compare_to: '',
                page: 1,
            });
            return;
        }

        this.paramService.updateParams({
            comparison: 'true',
            compare_from: this.toDateParam(value[0]),
            compare_to: this.toDateParam(value[1]),
            page: 1,
            page_size: this.config.comparisonMaxPageSize,
        });
    }

    formatNumber (value: number | null | undefined): string {
        return Number(value || 0).toLocaleString();
    }

    formatDuration (seconds: number | null | undefined): string {
        const safeSeconds = Math.max(0, Number(seconds || 0));
        const hours = Math.floor(safeSeconds / 3600);
        const minutes = Math.floor((safeSeconds % 3600) / 60);
        const remainingSeconds = Math.floor(safeSeconds % 60);

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    }

    getCellValue (row: SiteAnalyticsTableRow, field: string): SiteAnalyticsCellValue {
        return row[field] ?? null;
    }

    getLinkCell (row: SiteAnalyticsTableRow, field: string): SiteAnalyticsLinkCell | null {
        return this.toLinkCell(this.getCellValue(row, field));
    }

    getLinkAriaLabel (field: string, linkCell: SiteAnalyticsLinkCell): string {
        const column = this.getColumnConfig(field);
        if (!column?.ariaLabelKey) {
            return linkCell.label;
        }

        const destination = this.isStateLinkCell(linkCell) ? linkCell.state : linkCell.href;

        return this.translocoService.translate(column.ariaLabelKey, {
            url: destination,
            label: linkCell.label,
        });
    }

    isStateLinkCell (linkCell: SiteAnalyticsLinkCell): linkCell is SiteAnalyticsStateLinkCell {
        return linkCell.type === 'state';
    }

    isDurationColumn (field: string): boolean {
        return this.getColumnConfig(field)?.displayType === 'duration';
    }

    isNumberColumn (field: string): boolean {
        return this.getColumnConfig(field)?.displayType === 'number';
    }

    getNumericCellValue (row: SiteAnalyticsTableRow, field: string): number {
        const value = this.getCellValue(row, field);
        return this.toNumber(value);
    }

    getTextCellValue (row: SiteAnalyticsTableRow, field: string): string {
        const value = this.getCellValue(row, field);
        if (value === null) {
            return '-';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number') {
            return String(value);
        }
        return value.label;
    }

    private getRowStringValue (row: SiteAnalyticsTableRow, field: string): string {
        const value = row[field];

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'number') {
            return String(value);
        }

        return '';
    }

    private getColumnConfig (field: string): SiteAnalyticsColumnConfig | undefined {
        return this.config.columns.find((column) => column.id === field);
    }

    private initializeExportVariants (): void {
        const firstVariant = this.config.exportVariants?.[0] || null;
        this.selectedExportVariantId = firstVariant?.id || null;
    }

    private buildBreadcrumbItems (): MenuItem[] {
        if (!this.config.breadcrumbsBuilder) {
            return [];
        }

        const sourceItems = this.config.breadcrumbsBuilder(this.routeParams)
            .filter((item) => Boolean(item.label || item.labelKey));

        return sourceItems.map((item, index) => {
            const label = item.labelKey
                ? this.translocoService.translate(item.labelKey)
                : item.label || '';
            const isLast = index === sourceItems.length - 1;

            if (!isLast && item.state) {
                return {
                    label,
                    command: (): void => {
                        this.stateService.go(item.state as string, item.params || {});
                    },
                } as MenuItem;
            }

            return {
                label,
            } as MenuItem;
        });
    }

    private applyParams (params: Record<string, unknown>): void {
        this.routeParams = { ...params };
        this.breadcrumbItems = this.buildBreadcrumbItems();

        this.domainId = this.parsePositiveInteger(params.domainId, this.domainId);
        this.page = this.parsePositiveInteger(params.page, 1);
        this.perPage = this.parsePageSize(params.page_size, this.perPage || DEFAULT_PAGE_SIZE);
        this.searchTerm = this.isSearchSupported && typeof params.search === 'string' ? params.search : '';
        this.sortBy = typeof params.sort_by === 'string' && params.sort_by
            ? params.sort_by
            : this.config.defaultSortBy;
        this.sortDir = params.sort_dir === 'asc' || params.sort_dir === 'desc'
            ? params.sort_dir
            : this.config.defaultSortDir;

        const primaryRange = this.parseDateRange(params.from, params.to);
        if (primaryRange) {
            this.primaryDateRange = primaryRange;
        } else {
            const persistedRange = this.statisticsDateRangeService.getLastSelectedRange();
            const fallback = persistedRange ?? this.dateRangeService.setRangeDates();
            this.primaryDateRange = [
                fallback.startDate.toDate(),
                fallback.endDate.toDate(),
            ];
        }
        this.rememberPrimaryDateRange();

        this.comparisonEnabled = this.isComparisonSupported && params.comparison === 'true';
        this.comparisonDateRange = this.isComparisonSupported
            ? this.parseDateRange(params.compare_from, params.compare_to)
            : null;

        if (!this.comparisonEnabled) {
            this.nonComparisonPerPage = this.perPage;
        }
    }

    private async loadData (): Promise<void> {
        if (!this.hasCompleteDateRange(this.primaryDateRange) || !this.domainId) {
            return;
        }

        this.loading = true;

        try {
            const requestRange = this.toMomentDateRange(this.primaryDateRange);
            const comparisonRange = this.isComparisonSupported && this.comparisonEnabled && this.hasCompleteDateRange(this.comparisonDateRange)
                ? this.toMomentDateRange(this.comparisonDateRange)
                : null;

            const dataRequest = {
                dataSource: this.config.dataSource,
                domainId: this.domainId,
                page: this.page,
                perPage: comparisonRange ? this.config.comparisonMaxPageSize : this.perPage,
                search: this.searchTerm,
                sortBy: this.sortBy,
                sortDir: this.sortDir,
                dateRange: requestRange,
                routeParams: this.routeParams,
                ...(comparisonRange ? { comparisonDateRange: comparisonRange } : {}),
            };

            const data: SiteAnalyticsDataResult = await this.siteAnalyticsPageDataService.loadData(dataRequest);

            this.expandedHierarchyParentIds.clear();
            this.rows = data.rows;
            this.total = data.total;
            this.perPage = data.perPage;
            this.page = data.page;
            this.columnAggregates = data.columnAggregates || {};
            this.chartData = this.createChartData(data);
        } catch {
            this.expandedHierarchyParentIds.clear();
            this.rows = [];
            this.total = 0;
            this.columnAggregates = {};
            this.chartData = {
                labels: [],
                datasets: [],
            };
        } finally {
            this.loading = false;
        }
    }

    private createChartData (result: SiteAnalyticsDataResult): ChartData<'bar'> {
        const primaryColor = this.getCssVariable('--p-primary-color', '#2C73D9');
        const primaryColorLight = this.getCssVariable('--p-primary-300', '#A8C7F4');
        const comparisonColor = this.getCssVariable('--p-primary-300', '#A8C7F4');
        const comparisonColorLight = this.getCssVariable('--p-primary-100', '#DCE9FB');
        const isHorizontalChart = this.config.chart.orientation !== 'vertical';

        const datasets: ChartData<'bar'>['datasets'] = [
            {
                label: this.translocoService.translate(this.config.chart.primarySeriesLabelKey),
                data: result.chart.primaryValues,
                backgroundColor: this.createBarGradient(primaryColor, primaryColorLight, isHorizontalChart),
                hoverBackgroundColor: primaryColor,
                borderWidth: 0,
                borderRadius: this.getBarEndRadius(isHorizontalChart),
                borderSkipped: false,
                maxBarThickness: 28,
            },
        ];

        if (result.chart.comparisonValues && this.isComparisonModeActive) {
            datasets.push({
                label: this.translocoService.translate(this.config.chart.comparisonSeriesLabelKey),
                data: result.chart.comparisonValues,
                backgroundColor: this.createBarGradient(comparisonColor, comparisonColorLight, isHorizontalChart),
                hoverBackgroundColor: comparisonColor,
                borderWidth: 0,
                borderRadius: this.getBarEndRadius(isHorizontalChart),
                borderSkipped: false,
                maxBarThickness: 28,
            });
        }

        return {
            labels: result.chart.labels,
            datasets,
        };
    }

    /**
     * Only rounds the corners at the "end" of each bar (the tip pointing away from
     * the axis/origin) - i.e. the top corners for vertical bars, or the right-hand
     * corners for horizontal bars. The corners at the start/base of the bar (where
     * it meets the axis) stay square, matching a typical modern bar chart look.
     */
    private getBarEndRadius (isHorizontalChart: boolean): { topLeft: number; topRight: number; bottomLeft: number; bottomRight: number } {
        if (isHorizontalChart) {
            return { topLeft: 0, bottomLeft: 0, topRight: 4, bottomRight: 4 };
        }
        return { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };
    }

    /**
     * Builds a soft linear gradient fill for chart bars (solid accent color fading
     * to a lighter tint) instead of the previous flat, single-color fill. Falls back
     * to the solid color if the chart area isn't ready yet (e.g. first paint).
     */
    private createBarGradient (
        solidColor: string,
        lightColor: string,
        isHorizontalChart: boolean,
    ): (context: ScriptableContext<'bar'>) => string | CanvasGradient {
        return (context: ScriptableContext<'bar'>): string | CanvasGradient => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) {
                return solidColor;
            }

            const gradient = isHorizontalChart
                ? ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0)
                : ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(0, solidColor);
            gradient.addColorStop(1, lightColor);
            return gradient;
        };
    }

    private initializeChartOptions (): void {
        const borderColor = this.getCssVariable('--p-content-border-color', '#BDCED6');
        const tickColor = this.getCssVariable('--p-text-color', '#435A65');
        const surfaceColor = this.getCssVariable('--p-surface-900', '#1F2937');
        const isHorizontalChart = this.config.chart.orientation !== 'vertical';

        const numericScale = {
            beginAtZero: true,
            ticks: {
                color: tickColor,
                font: { size: 12 },
                callback: (value: string | number): string => this.formatChartValue(Number(value)),
            },
            grid: {
                color: borderColor,
                borderDash: [2, 2],
                tickBorderDash: [2, 2],
            },
            border: {
                display: false,
            },
        };

        const categoryScale = {
            ticks: {
                color: tickColor,
                font: { size: 12 },
                callback: (value: string | number, index: number): string => {
                    const label = this.chartData.labels?.[index];
                    return this.truncateChartLabel(typeof label === 'string' ? label : String(value));
                },
            },
            grid: {
                display: false,
            },
            border: {
                display: false,
            },
        };

        this.chartOptions = {
            indexAxis: isHorizontalChart ? 'y' : 'x',
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 300,
                easing: 'easeOutQuart',
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: tickColor,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        boxWidth: 8,
                        boxHeight: 8,
                        padding: 16,
                        font: { size: 12 },
                    },
                },
                tooltip: {
                    backgroundColor: surfaceColor,
                    padding: 10,
                    cornerRadius: 6,
                    displayColors: true,
                    boxPadding: 4,
                    titleFont: { size: 12, weight: 'bold' },
                    bodyFont: { size: 12 },
                    callbacks: {
                        label: (context): string => {
                            const label = context.dataset.label || '';
                            return `${label}: ${this.formatChartValue(Number(context.raw || 0))}`;
                        },
                    },
                },
            },
            scales: isHorizontalChart
                ? {
                    x: numericScale,
                    y: categoryScale,
                }
                : {
                    x: categoryScale,
                    y: numericScale,
                },
        };
    }

    private parsePositiveInteger (value: unknown, fallback: number): number {
        const parsed = Number.parseInt(String(value), 10);
        if (Number.isNaN(parsed) || parsed < 1) {
            return fallback;
        }
        return parsed;
    }

    private parsePageSize (value: unknown, fallback: number): number {
        const parsed = Number.parseInt(String(value), 10);
        if (!AVAILABLE_PAGE_SIZE.includes(parsed)) {
            return fallback;
        }
        return parsed;
    }

    private parseDateRange (from: unknown, to: unknown): [Date, Date] | null {
        if (typeof from !== 'string' || typeof to !== 'string' || !from || !to) {
            return null;
        }

        const fromDate = moment(from, STATISTICS.DATE_FORMAT, true);
        const toDate = moment(to, STATISTICS.DATE_FORMAT, true);

        if (!fromDate.isValid() || !toDate.isValid()) {
            return null;
        }

        return [fromDate.toDate(), toDate.toDate()];
    }

    private rememberPrimaryDateRange (): void {
        if (!this.hasCompleteDateRange(this.primaryDateRange)) {
            return;
        }

        this.statisticsDateRangeService.setLastSelectedRange({
            startDate: moment(this.primaryDateRange[0]),
            endDate: moment(this.primaryDateRange[1]),
        });
    }

    private hasCompleteDateRange (value: [Date | null, Date | null] | null): value is [Date, Date] {
        return Boolean(
            value &&
            value[0] instanceof Date &&
            value[1] instanceof Date,
        );
    }

    private toMomentDateRange (value: [Date, Date]): SiteAnalyticsDateRange {
        return {
            startDate: moment(value[0]),
            endDate: moment(value[1]),
        };
    }

    private toDateParam (value: Date): string {
        return moment(value).format(STATISTICS.DATE_FORMAT);
    }

    private toLinkCell (value: SiteAnalyticsCellValue): SiteAnalyticsLinkCell | null {
        if (!value || typeof value !== 'object' || !('label' in value) || typeof value.label !== 'string') {
            return null;
        }

        if (
            'type' in value &&
            value.type === 'state' &&
            'state' in value &&
            typeof value.state === 'string'
        ) {
            const params = 'params' in value && value.params && typeof value.params === 'object'
                ? value.params as Record<string, unknown>
                : undefined;

            return {
                type: 'state',
                label: value.label,
                state: value.state,
                ...(params ? { params } : {}),
            };
        }

        if ('href' in value && typeof value.href === 'string') {
            return {
                type: 'external',
                label: value.label,
                href: value.href,
                ...(typeof value.tooltip === 'string' && value.tooltip ? { tooltip: value.tooltip } : {}),
            };
        }

        return null;
    }

    private toNumber (value: SiteAnalyticsCellValue): number {
        if (typeof value === 'number') {
            return value;
        }

        if (typeof value === 'string') {
            const parsed = Number.parseFloat(value);
            return Number.isNaN(parsed) ? 0 : parsed;
        }

        return 0;
    }

    private formatChartValue (value: number): string {
        if (this.config.chart.valueDisplayFormat === 'percentage') {
            const safeValue = Number.isFinite(value) ? value : 0;
            return `${safeValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
        }

        return this.formatNumber(value);
    }

    private truncateChartLabel (label: string, maxLength: number = 28): string {
        if (label.length <= maxLength) {
            return label;
        }

        return `${label.slice(0, Math.max(0, maxLength - 1))}\u2026`;
    }

    private getCssVariable (name: string, fallback: string): string {
        const rawValue = getComputedStyle(this.document.documentElement).getPropertyValue(name);
        const value = typeof rawValue === 'string' ? rawValue.trim() : '';
        return value || fallback;
    }
}
